import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import type { BrandingLocalization } from "@/lib/api/review";
import { DetailField, FieldEditedBadge, type FieldEditInfo } from "./Shared";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { FilePreview } from "@/components/ui/FilePreview";

interface BrandingLocalizationTabProps {
  branding: BrandingLocalization | undefined;
  fieldEdits?: Record<string, FieldEditInfo>;
  onShowFieldEdit?: (fieldKey: string, titleKey: TranslationKeys) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function BrandingLocalizationTab({
  branding,
  fieldEdits,
  onShowFieldEdit,
  applicationId,
  t,
}: BrandingLocalizationTabProps) {
  const notProvided = t("review.application.brandingLocalization.notProvided");

  return (
    <View className="max-w-4xl items-start">
      <AppText weight="bold" className="text-xl text-dark-text-50 mb-2">
        {t("review.application.brandingLocalization.title")}
      </AppText>
      <AppText className="text-base text-dark-text-20 mb-8 leading-6">
        {t("review.application.brandingLocalization.description")}
      </AppText>

      <View className="h-px bg-stroke-20 mb-8 w-full" />

      {/* Short names — two column */}
      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.brandingLocalization.fields.shortNameEnglish")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {branding?.shortNameEnglish}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6">
          <DetailField label={t("review.application.brandingLocalization.fields.shortNameArabic")}>
            {branding?.shortNameArabic ? (
              <AppText weight="medium" className="text-base text-dark-text-50">
                {branding.shortNameArabic}
              </AppText>
            ) : (
              <AppText className="text-base text-dark-text-20 italic">{notProvided}</AppText>
            )}
          </DetailField>
        </View>
      </View>

      {/* Business description (English) — full width */}
      <DetailField label={t("review.application.brandingLocalization.fields.descriptionEnglish")}>
        {branding?.descriptionEnglish ? (
          <AppText weight="medium" className="text-base text-dark-text-50 leading-6">
            {branding.descriptionEnglish}
          </AppText>
        ) : (
          <AppText className="text-base text-dark-text-20 italic">{notProvided}</AppText>
        )}
        {fieldEdits?.["descriptionEnglish"] && onShowFieldEdit && (
          <FieldEditedBadge
            onShowEdit={() =>
              onShowFieldEdit(
                "descriptionEnglish",
                "review.application.brandingLocalization.fields.descriptionEnglish"
              )
            }
            t={t}
          />
        )}
      </DetailField>

      {/* Business description (Arabic) — full width */}
      <DetailField label={t("review.application.brandingLocalization.fields.descriptionArabic")}>
        {branding?.descriptionArabic ? (
          <AppText weight="medium" className="text-base text-dark-text-50 leading-6">
            {branding.descriptionArabic}
          </AppText>
        ) : (
          <AppText className="text-base text-dark-text-20 italic">{notProvided}</AppText>
        )}
      </DetailField>

      {/* Business logo */}
      <DetailField label={t("review.application.brandingLocalization.fields.businessLogo")}>
        {branding?.logoUrl ? (
          <FilePreview
            className="max-w-[320px]"
            fileKey={branding?.logoUrl}
            isVendor={false}
            applicationId={applicationId}
          />
        ) : (
          <View className="max-w-[229px] max-h-[160px] bg-white rounded-xl border border-stroke-30 items-center justify-center">
            <AppText className="text-sm text-dark-text-20 text-center">
              {t("review.application.brandingLocalization.noLogo")}
            </AppText>
          </View>
        )}
      </DetailField>
    </View>
  );
}
